import { useEffect, useState } from "react";
import { Loader2, Download, TrendingUp } from "lucide-react";
import ExcelJS from "exceljs";
import { api } from "../../lib/api";
import { Button } from "../ui/button";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

export const RentalProfitTab = () => {
  const [month, setMonth] = useState(() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`; });
  const [d, setD] = useState(null);
  useEffect(() => { if (!month) return; setD(null); api.rentalProfit(month).then(setD).catch(() => setD({ rows: [], totals: {} })); }, [month]);
  const exportXlsx = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(`ربح ${month}`, { views: [{ rightToLeft: true }] });
    ws.columns = [{ header: "المركبة", width: 18 }, { header: "النوع / الموديل", width: 28 }, { header: "الدخل", width: 12 }, { header: "المصاريف", width: 12 },
      { header: "منها صيانة الأسطول", width: 16 }, { header: "الصافي", width: 12 }, { header: "أيام التأجير", width: 12 }, { header: "الإشغال %", width: 10 }];
    d.rows.forEach((r) => ws.addRow([r.identifier, [r.vtype, r.model].filter(Boolean).join(" · "), r.income, r.expenses, r.maintenance, r.net, r.booked_days, r.occupancy]));
    ws.addRow(["الإجمالي", "", d.totals.income, d.totals.expenses, "", d.totals.net]).font = { bold: true };
    ws.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
    ws.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1D4ED8" } };
    const buf = await wb.xlsx.writeBuffer();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([buf])); a.download = `ربح-مركبات-التأجير-${month}.xlsx`; a.click();
  };
  const T = ({ label, v, tone }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-xs text-slate-500">{label}</p><p className={`mt-1 font-heading text-2xl font-extrabold ${tone}`}>{fmt(v)}</p></div>);
  return (
    <div className="space-y-4" data-testid="rental-profit-tab">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="me-auto flex items-center gap-2 font-heading font-bold"><TrendingUp className="h-4 w-4 text-emerald-600" /> ربح كل مركبة</h3>
        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="rental-profit-month" />
        <Button variant="outline" onClick={exportXlsx} disabled={!d?.rows?.length} className="gap-1.5" data-testid="rental-profit-export"><Download className="h-4 w-4" /> Excel</Button>
      </div>
      {!d ? <div className="grid h-40 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : <>
        <div className="grid gap-3 sm:grid-cols-3">
          <T label="الدخل (مدفوع فعلاً)" v={d.totals.income} tone="text-slate-900" />
          <T label="المصاريف" v={d.totals.expenses} tone="text-rose-600" />
          <T label="الصافي" v={d.totals.net} tone={d.totals.net >= 0 ? "text-emerald-600" : "text-rose-600"} />
        </div>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="rental-profit-table">
            <thead className="bg-slate-50 text-xs text-slate-500"><tr>{["المركبة", "الدخل", "المصاريف", "الصافي", "الإشغال"].map((h) => <th key={h} className="p-3 text-start font-semibold">{h}</th>)}</tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`rental-profit-row-${r.id}`}>
                  <td className="p-3"><b>{r.identifier}</b><div className="text-[11px] text-slate-400">{[r.vtype, r.model].filter(Boolean).join(" · ")}</div></td>
                  <td className="p-3">{fmt(r.income)}</td>
                  <td className="p-3 text-rose-600">{fmt(r.expenses)}{r.maintenance > 0 && <div className="text-[10px] text-slate-400">صيانة الأسطول {fmt(r.maintenance)}</div>}</td>
                  <td className={`p-3 font-bold ${r.net >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmt(r.net)}</td>
                  <td className="p-3"><div className="flex items-center gap-2"><div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-blue-500" style={{ width: `${r.occupancy}%` }} /></div><span className="text-xs">{r.occupancy}% · {r.booked_days} يوم</span></div></td>
                </tr>))}
              {!d.rows.length && <tr><td colSpan={5} className="p-8 text-center text-slate-400">لا توجد مركبات تأجير</td></tr>}
            </tbody>
          </table>
        </div>
      </>}
    </div>
  );
};
